import type * as React from "react";

import { cn } from "./cn";

/*
 * Tables: row rules in `border-border`, 13px medium muted heads, no zebra.
 * Cell padding reads the density tier (`px-cell-x py-cell-y`: 16/14px, 12/10px
 * compact), so `density="compact"` — or any `[data-density="compact"]`
 * ancestor — tightens every cell. Figures line up: the theme sets tabular
 * numbers on every table.
 *
 * On a phone a wide table becomes a `TableStack`: the same rows as a list,
 * each row's cells as label/value pairs. Render both and let the breakpoint
 * pick (`hidden sm:block` on the table's wrapper, `sm:hidden` on the stack).
 */

type TableDensity = "comfortable" | "compact";

function Table({
  className,
  density,
  ...props
}: React.ComponentProps<"table"> & {
  /** Tightens this table to the compact tier; unset follows the page. */
  density?: TableDensity;
}) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto"
    >
      <table
        data-slot="table"
        data-density={density}
        className={cn("group/table w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("[&_tr]:border-b [&_tr]:border-border", className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "border-t border-border bg-muted/50 font-medium [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "border-b border-border transition-colors hover:bg-muted/50 has-aria-expanded:bg-muted/50 data-[state=selected]:bg-muted",
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "h-10 px-cell-x text-left align-middle text-compact font-medium whitespace-nowrap text-muted-foreground in-data-[density=compact]:h-9 [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "px-cell-x py-cell-y align-middle [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

/* ── the phone pattern: stacked rows ─────────────────────────────────────── */

/** A table's rows as a list, for a phone. */
function TableStack({ className, ...props }: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="table-stack"
      role="list"
      className={cn("flex flex-col", className)}
      {...props}
    />
  );
}

/** One row: the rule between rows is the table's own. */
function TableStackRow({ className, ...props }: React.ComponentProps<"li">) {
  return (
    <li
      data-slot="table-stack-row"
      className={cn(
        "flex flex-col gap-2 border-b border-border px-cell-x py-cell-y last:border-b-0",
        className,
      )}
      {...props}
    />
  );
}

/** The row's primary line: its name, with a chip or an action at the end. */
function TableStackTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="table-stack-title"
      className={cn(
        "flex min-w-0 items-center justify-between gap-3 text-sm font-medium text-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** The row's other cells, as label/value pairs. */
function TableStackFields({ className, ...props }: React.ComponentProps<"dl">) {
  return (
    <dl
      data-slot="table-stack-fields"
      className={cn("flex flex-col gap-1.5", className)}
      {...props}
    />
  );
}

function TableStackField({
  label,
  className,
  children,
  ...props
}: Omit<React.ComponentProps<"div">, "title"> & {
  /** The column head this value sits under in the wide table. */
  label: React.ReactNode;
}) {
  return (
    <div
      data-slot="table-stack-field"
      className={cn(
        "flex min-w-0 items-baseline justify-between gap-4 text-sm",
        className,
      )}
      {...props}
    >
      <dt className="shrink-0 text-compact text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right text-foreground tabular-nums">
        {children}
      </dd>
    </div>
  );
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableStack,
  TableStackField,
  TableStackFields,
  TableStackRow,
  TableStackTitle,
};
export type { TableDensity };
